import React, { useState, useMemo } from "react";
import {
  MdBusiness, MdAdd, MdClose, MdSearch, MdEdit, MdDelete,
  MdCheckCircle, MdWarning, MdBlock, MdArrowBack, MdInfo,
} from "react-icons/md";
import {
  FiUsers, FiMap, FiSettings, FiBarChart2, FiCreditCard, FiEye,
} from "react-icons/fi";
import { siteDummyData } from "../../Data/SiteData";
import { useRole } from "../../context/RoleContext";
import { canCreateOrg, canEditOrg, canSuspendOrg, canCreateSite, canInviteUser } from "../../utils/permissions";
import "./OrgManagement.css";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PLAN_META = {
  Enterprise:    { color: "#7c3aed", bg: "#ede9fe", label: "Enterprise" },
  Professional:  { color: "#0ea5e9", bg: "#e0f2fe", label: "Professional" },
  Basic:         { color: "#64748b", bg: "#f1f5f9", label: "Basic" },
};

const STATUS_META = {
  Active:   { color: "#16a34a", bg: "#dcfce7", icon: <MdCheckCircle size={13} /> },
  Trial:    { color: "#d97706", bg: "#fef3c7", icon: <MdWarning size={13} /> },
  Inactive: { color: "#dc2626", bg: "#fee2e2", icon: <MdBlock size={13} /> },
};

const SOL_COLORS = {
  STP:        "#8b5cf6",
  AQM:        "#10b981",
  Irrigation: "#f59e0b",
  Water:      "#3b82f6",
  Facility:   "#667eea",
  Fleet:      "#f97316",
};

const INDUSTRY_ICONS = {
  Industrial:    "🏭",
  "IT/Commercial": "💻",
  Agriculture:   "🌾",
  "Real Estate": "🏢",
};

const ALL_SOLUTIONS = ["STP", "AQM", "Irrigation", "Water", "Facility", "Fleet"];
const ALL_PLANS     = ["Basic", "Professional", "Enterprise"];
const ALL_STATUSES  = ["Active", "Trial", "Inactive"];
const ALL_TYPES     = ["Industrial", "IT/Commercial", "Agriculture", "Real Estate", "Healthcare", "Education", "Retail", "Other"];

function orgAvatar(org) {
  const letter = org.name.charAt(0).toUpperCase();
  const colors = ["#7c3aed", "#0ea5e9", "#16a34a", "#f59e0b", "#ef4444", "#06b6d4", "#d946ef", "#f97316"];
  const idx = org.id.replace("ORG-", "") - 1;
  return { letter, color: colors[idx % colors.length] };
}

function getOrgStats(org) {
  const sites = siteDummyData.sites.filter(s => org.siteIds.includes(s.id));
  let deviceCount = 0, alertCount = 0, healthSum = 0, onlineDevices = 0, totalDevices = 0;
  sites.forEach(site => {
    deviceCount  += (site.devices || []).length;
    alertCount   += (site.alerts  || []).filter(a => a.status === "Active").length;
    (site.devices || []).forEach(d => {
      healthSum += d.health;
      totalDevices++;
      if (d.status === "Online") onlineDevices++;
    });
  });
  const health = totalDevices > 0 ? Math.round(healthSum / totalDevices) : 0;
  const users = sites.reduce((acc, s) => acc + (s.users || []).length, 0);
  return { siteCount: sites.length, deviceCount, alertCount, health, onlineDevices, totalDevices, users };
}

// ─── Small reusable chips/badges ─────────────────────────────────────────────

function PlanBadge({ plan }) {
  const m = PLAN_META[plan] || PLAN_META.Basic;
  return (
    <span className="org-badge" style={{ background: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.Inactive;
  return (
    <span className="org-badge org-badge-icon" style={{ background: m.bg, color: m.color }}>
      {m.icon} {status}
    </span>
  );
}

function SolChip({ sol }) {
  return (
    <span className="org-sol-chip" style={{ background: (SOL_COLORS[sol] || "#64748b") + "22", color: SOL_COLORS[sol] || "#64748b" }}>
      {sol}
    </span>
  );
}

function HealthDot({ score }) {
  const c = score >= 80 ? "#16a34a" : score >= 50 ? "#d97706" : "#dc2626";
  return <span className="org-health-dot" style={{ background: c }} title={`Health ${score}%`} />;
}

// ─── Create / Edit Organization Modal ────────────────────────────────────────

const EMPTY_FORM = {
  name: "", type: "Industrial", registration: "", gst: "",
  address: "", contact: "", email: "", phone: "", description: "",
  plan: "Basic", maxSites: 3, maxDevices: 50,
  solutionsEnabled: [], dataRetention: "6 months", slaLevel: "98.0%", apiAccess: false,
  adminName: "", adminEmail: "", adminPhone: "",
  timezone: "Asia/Kolkata", notificationDefaults: [],
};

function CreateOrgModal({ onClose, onSave, editOrg }) {
  const [section, setSection] = useState(0);
  const [form, setForm] = useState(editOrg ? {
    ...EMPTY_FORM,
    name: editOrg.name, type: editOrg.type,
    registration: editOrg.registration || "", gst: editOrg.gst || "",
    address: editOrg.address || "", contact: editOrg.contact || "",
    email: editOrg.email || "", phone: editOrg.phone || "",
    description: editOrg.description || "",
    plan: editOrg.plan, maxSites: editOrg.maxSites, maxDevices: editOrg.maxDevices,
    solutionsEnabled: editOrg.solutionsEnabled || [],
    dataRetention: editOrg.dataRetention || "6 months",
    slaLevel: editOrg.slaLevel || "98.0%", apiAccess: editOrg.apiAccess || false,
    adminName: editOrg.adminUser?.name || "", adminEmail: editOrg.adminUser?.email || "",
    adminPhone: "", timezone: editOrg.timezone || "Asia/Kolkata",
    notificationDefaults: editOrg.notificationDefaults || [],
  } : { ...EMPTY_FORM });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleSol = sol => {
    const cur = form.solutionsEnabled;
    set("solutionsEnabled", cur.includes(sol) ? cur.filter(x => x !== sol) : [...cur, sol]);
  };

  const toggleNotif = ch => {
    const cur = form.notificationDefaults;
    set("notificationDefaults", cur.includes(ch) ? cur.filter(x => x !== ch) : [...cur, ch]);
  };

  const planChange = plan => {
    const defaults = { Basic: [50, 3], Professional: [150, 10], Enterprise: [500, 20] };
    const [devices, sites] = defaults[plan];
    setForm(f => ({ ...f, plan, maxDevices: devices, maxSites: sites }));
  };

  const sections = ["Basic Info", "Subscription", "Admin User", "Advanced"];

  return (
    <div className="org-modal-overlay" onClick={onClose}>
      <div className="org-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="org-modal-header">
          <div className="org-modal-title">
            <MdBusiness size={20} />
            <span>{editOrg ? "Edit Organization" : "Create New Organization"}</span>
          </div>
          <button className="org-modal-close" onClick={onClose}><MdClose size={18} /></button>
        </div>

        {/* Section nav */}
        <div className="org-modal-section-nav">
          {sections.map((s, i) => (
            <button
              key={i}
              className={`org-modal-section-btn ${section === i ? "org-modal-section-active" : ""}`}
              onClick={() => setSection(i)}
            >
              <span className="org-modal-section-num">{i + 1}</span> {s}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="org-modal-body">

          {/* ── Section 0: Basic Info ── */}
          {section === 0 && (
            <div className="org-modal-grid">
              <div className="org-form-group org-span-2">
                <label>Organization Name *</label>
                <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Tata Industries Ltd." />
              </div>
              <div className="org-form-group">
                <label>Industry / Type *</label>
                <select value={form.type} onChange={e => set("type", e.target.value)}>
                  {ALL_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="org-form-group">
                <label>Company Registration No.</label>
                <input value={form.registration} onChange={e => set("registration", e.target.value)} placeholder="U27100MH1989PLC052270" />
              </div>
              <div className="org-form-group">
                <label>GST Number</label>
                <input value={form.gst} onChange={e => set("gst", e.target.value)} placeholder="27AABCT3518Q1ZC" />
              </div>
              <div className="org-form-group">
                <label>Primary Contact</label>
                <input value={form.contact} onChange={e => set("contact", e.target.value)} placeholder="Full name" />
              </div>
              <div className="org-form-group">
                <label>Contact Email *</label>
                <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="admin@company.com" />
              </div>
              <div className="org-form-group">
                <label>Phone</label>
                <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+91-XX-XXXX-XXXX" />
              </div>
              <div className="org-form-group org-span-2">
                <label>Registered Address</label>
                <textarea value={form.address} onChange={e => set("address", e.target.value)} placeholder="Full registered address" rows={2} />
              </div>
              <div className="org-form-group org-span-2">
                <label>Description</label>
                <textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="Brief description of the organization and deployment scope" rows={2} />
              </div>
            </div>
          )}

          {/* ── Section 1: Subscription ── */}
          {section === 1 && (
            <div className="org-modal-grid">
              <div className="org-form-group org-span-2">
                <label>Subscription Plan *</label>
                <div className="org-plan-cards">
                  {ALL_PLANS.map(p => {
                    const m = PLAN_META[p];
                    return (
                      <div
                        key={p}
                        className={`org-plan-card ${form.plan === p ? "org-plan-card-active" : ""}`}
                        style={form.plan === p ? { borderColor: m.color } : {}}
                        onClick={() => planChange(p)}
                      >
                        <div className="org-plan-card-name" style={{ color: m.color }}>{p}</div>
                        <div className="org-plan-card-desc">
                          {p === "Basic"        && "Up to 3 sites · 50 devices"}
                          {p === "Professional" && "Up to 10 sites · 150 devices"}
                          {p === "Enterprise"   && "Up to 20 sites · 500 devices"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="org-form-group">
                <label>Max Sites</label>
                <input type="number" value={form.maxSites} onChange={e => set("maxSites", +e.target.value)} min={1} />
              </div>
              <div className="org-form-group">
                <label>Max Devices</label>
                <input type="number" value={form.maxDevices} onChange={e => set("maxDevices", +e.target.value)} min={1} />
              </div>
              <div className="org-form-group">
                <label>Data Retention</label>
                <select value={form.dataRetention} onChange={e => set("dataRetention", e.target.value)}>
                  {["3 months", "6 months", "12 months", "18 months", "24 months", "36 months"].map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div className="org-form-group">
                <label>SLA Target</label>
                <select value={form.slaLevel} onChange={e => set("slaLevel", e.target.value)}>
                  {["97.0%", "98.0%", "98.5%", "99.0%", "99.5%"].map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div className="org-form-group org-span-2">
                <label>Solutions Enabled</label>
                <div className="org-sol-toggle-group">
                  {ALL_SOLUTIONS.map(sol => (
                    <button
                      key={sol}
                      className={`org-sol-toggle ${form.solutionsEnabled.includes(sol) ? "org-sol-toggle-on" : ""}`}
                      style={form.solutionsEnabled.includes(sol) ? { background: SOL_COLORS[sol] + "22", borderColor: SOL_COLORS[sol], color: SOL_COLORS[sol] } : {}}
                      onClick={() => toggleSol(sol)}
                      type="button"
                    >
                      {sol}
                    </button>
                  ))}
                </div>
              </div>
              <div className="org-form-group org-span-2">
                <label className="org-toggle-label">
                  <input type="checkbox" checked={form.apiAccess} onChange={e => set("apiAccess", e.target.checked)} />
                  <span>Enable API Access (REST / MQTT)</span>
                </label>
              </div>
            </div>
          )}

          {/* ── Section 2: Admin User ── */}
          {section === 2 && (
            <div className="org-modal-grid">
              <div className="org-info-box org-span-2">
                <MdInfo size={15} />
                <span>The Org Admin can manage all sites and users within this organization. They receive billing notifications and critical alerts.</span>
              </div>
              <div className="org-form-group">
                <label>Admin Full Name *</label>
                <input value={form.adminName} onChange={e => set("adminName", e.target.value)} placeholder="Full name" />
              </div>
              <div className="org-form-group">
                <label>Admin Email *</label>
                <input type="email" value={form.adminEmail} onChange={e => set("adminEmail", e.target.value)} placeholder="admin@company.com" />
              </div>
              <div className="org-form-group">
                <label>Admin Phone</label>
                <input value={form.adminPhone} onChange={e => set("adminPhone", e.target.value)} placeholder="+91-XXXXX-XXXXX" />
              </div>
              <div className="org-form-group">
                <label>Role</label>
                <input value="Org Admin" disabled />
              </div>
              <div className="org-info-box org-span-2" style={{ marginTop: 8 }}>
                <MdInfo size={15} />
                <span>An invitation email will be sent to the admin email address to set up their password.</span>
              </div>
            </div>
          )}

          {/* ── Section 3: Advanced ── */}
          {section === 3 && (
            <div className="org-modal-grid">
              <div className="org-form-group org-span-2">
                <label>Timezone</label>
                <select value={form.timezone} onChange={e => set("timezone", e.target.value)}>
                  {["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "UTC"].map(tz => <option key={tz}>{tz}</option>)}
                </select>
              </div>
              <div className="org-form-group org-span-2">
                <label>Default Notification Channels</label>
                <div className="org-sol-toggle-group">
                  {["SMS", "Email", "WhatsApp", "Push"].map(ch => (
                    <button
                      key={ch}
                      className={`org-sol-toggle ${form.notificationDefaults.includes(ch) ? "org-sol-toggle-on" : ""}`}
                      style={form.notificationDefaults.includes(ch) ? { background: "#667eea22", borderColor: "#667eea", color: "#667eea" } : {}}
                      onClick={() => toggleNotif(ch)}
                      type="button"
                    >
                      {ch}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="org-modal-footer">
          <div className="org-modal-section-arrows">
            {section > 0 && (
              <button className="org-btn org-btn-ghost" onClick={() => setSection(s => s - 1)}>← Back</button>
            )}
            {section < sections.length - 1 && (
              <button className="org-btn org-btn-primary" onClick={() => setSection(s => s + 1)}>Next →</button>
            )}
            {section === sections.length - 1 && (
              <button className="org-btn org-btn-primary" onClick={() => onSave(form)}>
                {editOrg ? "Save Changes" : "Create Organization"}
              </button>
            )}
          </div>
          <button className="org-btn org-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Org Dashboard: Overview tab ─────────────────────────────────────────────

function OvwTab({ org, stats }) {
  const av = orgAvatar(org);
  return (
    <div className="org-ovw-root">
      {/* Hero row */}
      <div className="org-ovw-hero">
        <div className="org-ovw-avatar-wrap">
          <div className="org-ovw-avatar" style={{ background: av.color }}>{av.letter}</div>
        </div>
        <div className="org-ovw-hero-info">
          <h2 className="org-ovw-hero-name">{org.name}</h2>
          <p className="org-ovw-hero-desc">{org.description}</p>
          <div className="org-ovw-hero-badges">
            <span className="org-badge" style={{ background: "#1e293b", color: "#94a3b8" }}>
              {INDUSTRY_ICONS[org.type] || "🏢"} {org.type}
            </span>
            <PlanBadge plan={org.plan} />
            <StatusBadge status={org.status} />
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="org-ovw-kpis">
        {[
          { label: "Sites",     value: stats.siteCount,    sub: `of ${org.maxSites} max`,   color: "#667eea" },
          { label: "Devices",   value: stats.deviceCount,  sub: `${stats.onlineDevices} online`, color: "#10b981" },
          { label: "Users",     value: stats.users,        sub: "active members",            color: "#0ea5e9" },
          { label: "Alerts",    value: stats.alertCount,   sub: "active now",                color: stats.alertCount > 0 ? "#dc2626" : "#16a34a" },
          { label: "Health",    value: `${stats.health}%`, sub: "avg device health",         color: stats.health >= 80 ? "#16a34a" : stats.health >= 50 ? "#d97706" : "#dc2626" },
        ].map(k => (
          <div className="org-ovw-kpi" key={k.label}>
            <div className="org-ovw-kpi-value" style={{ color: k.color }}>{k.value}</div>
            <div className="org-ovw-kpi-label">{k.label}</div>
            <div className="org-ovw-kpi-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Info cards row */}
      <div className="org-ovw-cards">
        {/* Contact */}
        <div className="org-ovw-card">
          <div className="org-ovw-card-title">Contact</div>
          <div className="org-ovw-card-row"><span>Name</span><strong>{org.contact}</strong></div>
          <div className="org-ovw-card-row"><span>Email</span><strong>{org.email}</strong></div>
          <div className="org-ovw-card-row"><span>Phone</span><strong>{org.phone}</strong></div>
          <div className="org-ovw-card-row"><span>Timezone</span><strong>{org.timezone}</strong></div>
        </div>
        {/* Registration */}
        <div className="org-ovw-card">
          <div className="org-ovw-card-title">Registration</div>
          <div className="org-ovw-card-row"><span>CIN / Reg No.</span><strong>{org.registration}</strong></div>
          <div className="org-ovw-card-row"><span>GST</span><strong>{org.gst}</strong></div>
          <div className="org-ovw-card-row"><span>Created</span><strong>{org.createdAt}</strong></div>
          <div className="org-ovw-card-row"><span>Address</span><strong style={{ fontSize: 11, lineHeight: 1.4 }}>{org.address}</strong></div>
        </div>
        {/* Solutions */}
        <div className="org-ovw-card">
          <div className="org-ovw-card-title">Active Solutions</div>
          <div className="org-sol-chips-wrap">
            {(org.solutionsEnabled || []).map(s => <SolChip key={s} sol={s} />)}
          </div>
          <div className="org-ovw-card-row" style={{ marginTop: 12 }}><span>SLA Target</span><strong>{org.slaLevel}</strong></div>
          <div className="org-ovw-card-row"><span>Data Retention</span><strong>{org.dataRetention}</strong></div>
          <div className="org-ovw-card-row"><span>API Access</span><strong>{org.apiAccess ? "✅ Enabled" : "❌ Disabled"}</strong></div>
        </div>
      </div>
    </div>
  );
}

// ─── Org Dashboard: Sites tab ─────────────────────────────────────────────────

function SitesTab({ org }) {
  const { currentRole } = useRole();
  const sites = siteDummyData.sites.filter(s => org.siteIds.includes(s.id));
  return (
    <div className="org-tab-section">
      <div className="org-tab-section-hdr">
        <span>{sites.length} site{sites.length !== 1 ? "s" : ""} in this organization</span>
        {canCreateSite(currentRole) && (
          <button className="org-btn org-btn-primary org-btn-sm"><MdAdd size={14} /> Add Site</button>
        )}
      </div>
      <div className="org-sites-grid">
        {sites.map(site => {
          const activeAlerts = (site.alerts || []).filter(a => a.status === "Active").length;
          return (
            <div className="org-site-card" key={site.id}>
              <div className="org-site-card-top">
                <div>
                  <div className="org-site-card-name">{site.name}</div>
                  <div className="org-site-card-id">{site.id} · {site.type}</div>
                </div>
                <HealthDot score={site.healthScore} />
              </div>
              <div className="org-site-card-meta">
                <span>{(site.devices || []).length} devices</span>
                <span>{(site.users || []).length} users</span>
                {activeAlerts > 0 && <span className="org-alert-chip">{activeAlerts} alert{activeAlerts > 1 ? "s" : ""}</span>}
              </div>
              <div className="org-site-card-sols">
                {(site.solutions || []).filter(s => s.enabled).map(s => <SolChip key={s.id} sol={s.id} />)}
              </div>
              <div className="org-site-card-footer">
                <span className="org-site-card-plan"><PlanBadge plan={site.plan} /></span>
                <StatusBadge status={site.status} />
              </div>
            </div>
          );
        })}
        {sites.length < org.maxSites && canCreateSite(currentRole) && (
          <div className="org-site-card org-site-add">
            <MdAdd size={28} />
            <span>Add New Site</span>
            <small>{org.maxSites - sites.length} slots remaining</small>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Org Dashboard: Users tab ─────────────────────────────────────────────────

function UsersTab({ org }) {
  const { currentRole } = useRole();
  const sites = siteDummyData.sites.filter(s => org.siteIds.includes(s.id));
  const users = sites.flatMap(site =>
    (site.users || []).map(u => ({ ...u, siteName: site.name, siteId: site.id }))
  );
  return (
    <div className="org-tab-section">
      <div className="org-tab-section-hdr">
        <span>{users.length} user{users.length !== 1 ? "s" : ""} across {sites.length} site{sites.length !== 1 ? "s" : ""}</span>
        {canInviteUser(currentRole) && (
          <button className="org-btn org-btn-primary org-btn-sm"><MdAdd size={14} /> Invite User</button>
        )}
      </div>
      <table className="org-table">
        <thead>
          <tr>
            <th>Name</th><th>Email</th><th>Role</th><th>Site</th><th>Status</th><th>Last Login</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id}>
              <td><strong>{u.name}</strong></td>
              <td className="org-table-mono">{u.email}</td>
              <td><span className="org-role-chip">{u.role}</span></td>
              <td>
                <span className="org-site-ref">{u.siteName}</span>
              </td>
              <td>
                <span className="org-status-dot" style={{ background: u.status === "Active" ? "#16a34a" : "#dc2626" }} />
                {u.status}
              </td>
              <td className="org-table-dim">{u.lastLogin}</td>
            </tr>
          ))}
          <tr className="org-admin-row">
            <td><strong>{org.adminUser?.name}</strong></td>
            <td className="org-table-mono">{org.adminUser?.email}</td>
            <td><span className="org-role-chip org-role-admin">Org Admin</span></td>
            <td><span className="org-site-ref">— (All sites)</span></td>
            <td>
              <span className="org-status-dot" style={{ background: "#16a34a" }} />Active
            </td>
            <td className="org-table-dim">—</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── Org Dashboard: Subscription tab ─────────────────────────────────────────

function SubTab({ org, stats }) {
  const usedSites   = stats.siteCount;
  const usedDevices = stats.deviceCount;
  const pctSites    = Math.min(100, Math.round((usedSites   / org.maxSites)   * 100));
  const pctDevices  = Math.min(100, Math.round((usedDevices / org.maxDevices) * 100));
  const m = PLAN_META[org.plan] || PLAN_META.Basic;

  return (
    <div className="org-tab-section">
      <div className="org-sub-plan-card">
        <div className="org-sub-plan-name" style={{ color: m.color }}>{org.plan} Plan</div>
        <div className="org-sub-plan-meta">
          <StatusBadge status={org.status} />
          <span className="org-table-dim">Since {org.createdAt}</span>
        </div>
      </div>

      <div className="org-sub-usage-grid">
        {/* Sites usage */}
        <div className="org-sub-usage-card">
          <div className="org-sub-usage-label">Sites Used</div>
          <div className="org-sub-usage-nums"><strong>{usedSites}</strong> / {org.maxSites}</div>
          <div className="org-sub-bar-wrap">
            <div className="org-sub-bar" style={{ width: `${pctSites}%`, background: pctSites > 90 ? "#ef4444" : m.color }} />
          </div>
          <div className="org-sub-usage-pct">{pctSites}% utilized</div>
        </div>
        {/* Devices usage */}
        <div className="org-sub-usage-card">
          <div className="org-sub-usage-label">Devices Used</div>
          <div className="org-sub-usage-nums"><strong>{usedDevices}</strong> / {org.maxDevices}</div>
          <div className="org-sub-bar-wrap">
            <div className="org-sub-bar" style={{ width: `${pctDevices}%`, background: pctDevices > 90 ? "#ef4444" : m.color }} />
          </div>
          <div className="org-sub-usage-pct">{pctDevices}% utilized</div>
        </div>
      </div>

      <div className="org-sub-details-grid">
        <div className="org-ovw-card">
          <div className="org-ovw-card-title">Entitlements</div>
          <div className="org-ovw-card-row"><span>Solutions</span><strong>{(org.solutionsEnabled || []).join(", ")}</strong></div>
          <div className="org-ovw-card-row"><span>Data Retention</span><strong>{org.dataRetention}</strong></div>
          <div className="org-ovw-card-row"><span>SLA Target</span><strong>{org.slaLevel}</strong></div>
          <div className="org-ovw-card-row"><span>API Access</span><strong>{org.apiAccess ? "✅ Yes" : "❌ No"}</strong></div>
        </div>
        <div className="org-ovw-card">
          <div className="org-ovw-card-title">Notification Channels</div>
          <div className="org-sol-chips-wrap" style={{ paddingTop: 8 }}>
            {(org.notificationDefaults || []).length > 0
              ? (org.notificationDefaults || []).map(c => (
                  <span key={c} className="org-badge" style={{ background: "#e0e7ff", color: "#3730a3" }}>{c}</span>
                ))
              : <span className="org-table-dim">None configured</span>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Org Dashboard: Settings tab ─────────────────────────────────────────────

function SettingsTab({ org, onEdit }) {
  const { currentRole } = useRole();
  return (
    <div className="org-tab-section">
      <div className="org-settings-grid">
        <div className="org-ovw-card">
          <div className="org-ovw-card-title">Organization Details</div>
          <div className="org-ovw-card-row"><span>Name</span><strong>{org.name}</strong></div>
          <div className="org-ovw-card-row"><span>Type</span><strong>{org.type}</strong></div>
          <div className="org-ovw-card-row"><span>Timezone</span><strong>{org.timezone}</strong></div>
          <div className="org-ovw-card-row"><span>Registration</span><strong>{org.registration}</strong></div>
          <div className="org-ovw-card-row"><span>GST</span><strong>{org.gst}</strong></div>
          {canEditOrg(currentRole) && (
            <button className="org-btn org-btn-outline org-btn-sm" style={{ marginTop: 14 }} onClick={onEdit}>
              <MdEdit size={13} /> Edit Details
            </button>
          )}
        </div>
        <div className="org-ovw-card">
          <div className="org-ovw-card-title">Org Admin</div>
          <div className="org-ovw-card-row"><span>Name</span><strong>{org.adminUser?.name}</strong></div>
          <div className="org-ovw-card-row"><span>Email</span><strong>{org.adminUser?.email}</strong></div>
          <div className="org-ovw-card-row"><span>Role</span><strong>{org.adminUser?.role}</strong></div>
          {canEditOrg(currentRole) && (
            <button className="org-btn org-btn-outline org-btn-sm" style={{ marginTop: 14 }}>
              <MdEdit size={13} /> Change Admin
            </button>
          )}
        </div>
      </div>
      {canSuspendOrg(currentRole) && (
        <div className="org-danger-zone">
          <div className="org-danger-title">Danger Zone</div>
          <div className="org-danger-row">
            <div>
              <strong>Suspend Organization</strong>
              <p>All sites and devices will be put into maintenance mode. No data will be lost.</p>
            </div>
            <button className="org-btn org-btn-warning org-btn-sm">Suspend</button>
          </div>
          <div className="org-danger-row">
            <div>
              <strong>Delete Organization</strong>
              <p>Permanently deletes all data. This action cannot be undone.</p>
            </div>
            <button className="org-btn org-btn-danger org-btn-sm"><MdDelete size={13} /> Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Org Dashboard View ───────────────────────────────────────────────────────

const ORG_TABS = [
  { id: "overview",      label: "Overview",     icon: <FiEye size={14} /> },
  { id: "sites",         label: "Sites",        icon: <FiMap size={14} /> },
  { id: "users",         label: "Users",        icon: <FiUsers size={14} /> },
  { id: "subscription",  label: "Subscription", icon: <FiCreditCard size={14} /> },
  { id: "reports",       label: "Reports",      icon: <FiBarChart2 size={14} /> },
  { id: "settings",      label: "Settings",     icon: <FiSettings size={14} /> },
];

function OrgDashboardView({ org, onBack, onEdit }) {
  const [tab, setTab] = useState("overview");
  const stats = useMemo(() => getOrgStats(org), [org]);
  const av    = orgAvatar(org);
  const { currentRole } = useRole();

  return (
    <div className="org-dashboard-root">
      {/* Top bar */}
      <div className="org-dashboard-topbar">
        <button className="org-back-btn" onClick={onBack}>
          <MdArrowBack size={16} /> Organizations
        </button>
        <div className="org-dashboard-topbar-title">
          <div className="org-mini-avatar" style={{ background: av.color }}>{av.letter}</div>
          <span>{org.name}</span>
          <StatusBadge status={org.status} />
        </div>
        {canEditOrg(currentRole) && (
          <button className="org-btn org-btn-outline org-btn-sm" onClick={() => onEdit(org)}>
            <MdEdit size={13} /> Edit
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div className="org-tab-bar">
        {ORG_TABS.map(t => (
          <button
            key={t.id}
            className={`org-tab ${tab === t.id ? "org-tab-active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.icon} {t.label}
            {t.id === "sites"  && <span className="org-tab-badge">{stats.siteCount}</span>}
            {t.id === "users"  && <span className="org-tab-badge">{stats.users}</span>}
          </button>
        ))}
      </div>

      {/* Panel */}
      <div className="org-dashboard-panel">
        {tab === "overview"      && <OvwTab org={org} stats={stats} />}
        {tab === "sites"         && <SitesTab org={org} />}
        {tab === "users"         && <UsersTab org={org} />}
        {tab === "subscription"  && <SubTab org={org} stats={stats} />}
        {tab === "reports"       && (
          <div className="org-tab-section">
            <div className="org-empty-state">
              <FiBarChart2 size={40} />
              <h3>Reports</h3>
              <p>Monthly consumption, alert trends, and device health reports will appear here.</p>
            </div>
          </div>
        )}
        {tab === "settings"      && <SettingsTab org={org} onEdit={() => onEdit(org)} />}
      </div>
    </div>
  );
}

// ─── Organization List View ───────────────────────────────────────────────────

function OrgListView({ orgs, onSelect, onAdd, onEdit }) {
  const { currentRole } = useRole();
  const [search, setSearch]     = useState("");
  const [filterType, setFType]  = useState("all");
  const [filterPlan, setFPlan]  = useState("all");
  const [filterStat, setFStat]  = useState("all");

  const types = useMemo(() => ["all", ...new Set(orgs.map(o => o.type))], [orgs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return orgs.filter(o => {
      if (q && !o.name.toLowerCase().includes(q) && !o.contact?.toLowerCase().includes(q)) return false;
      if (filterType !== "all" && o.type !== filterType) return false;
      if (filterPlan !== "all" && o.plan !== filterPlan) return false;
      if (filterStat !== "all" && o.status !== filterStat) return false;
      return true;
    });
  }, [orgs, search, filterType, filterPlan, filterStat]);

  return (
    <div className="org-list-root">
      {/* Header */}
      <div className="org-list-header">
        <div>
          <h1 className="org-list-title"><MdBusiness size={22} /> Organization Management</h1>
          <p className="org-list-sub">Manage all customer organizations, subscriptions, and access.</p>
        </div>
        {canCreateOrg(currentRole) && (
          <button className="org-btn org-btn-primary" onClick={onAdd}>
            <MdAdd size={16} /> Create Organization
          </button>
        )}
      </div>

      {/* Summary pills */}
      <div className="org-list-summary">
        {[
          { label: "Total",    value: orgs.length,                                 color: "#667eea" },
          { label: "Active",   value: orgs.filter(o => o.status === "Active").length,   color: "#16a34a" },
          { label: "Trial",    value: orgs.filter(o => o.status === "Trial").length,    color: "#d97706" },
          { label: "Inactive", value: orgs.filter(o => o.status === "Inactive").length, color: "#dc2626" },
        ].map(p => (
          <div className="org-summary-pill" key={p.label}>
            <span className="org-summary-val" style={{ color: p.color }}>{p.value}</span>
            <span className="org-summary-lbl">{p.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="org-list-filters">
        <div className="org-search-wrap">
          <MdSearch size={16} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search organizations…" />
        </div>
        <select value={filterType} onChange={e => setFType(e.target.value)}>
          {types.map(t => <option key={t} value={t}>{t === "all" ? "All Types" : t}</option>)}
        </select>
        <select value={filterPlan} onChange={e => setFPlan(e.target.value)}>
          <option value="all">All Plans</option>
          {ALL_PLANS.map(p => <option key={p}>{p}</option>)}
        </select>
        <select value={filterStat} onChange={e => setFStat(e.target.value)}>
          <option value="all">All Statuses</option>
          {ALL_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="org-table-wrap">
        <table className="org-table">
          <thead>
            <tr>
              <th>Organization</th>
              <th>Industry</th>
              <th>Sites</th>
              <th>Devices</th>
              <th>Users</th>
              <th>Active Alerts</th>
              <th>Health</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(org => {
              const stats = getOrgStats(org);
              const av    = orgAvatar(org);
              return (
                <tr key={org.id} className="org-table-row" onClick={() => onSelect(org)}>
                  <td>
                    <div className="org-table-name-cell">
                      <div className="org-mini-avatar" style={{ background: av.color }}>{av.letter}</div>
                      <div>
                        <div className="org-table-name">{org.name}</div>
                        <div className="org-table-id">{org.id}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="org-type-chip">{INDUSTRY_ICONS[org.type] || "🏢"} {org.type}</span>
                  </td>
                  <td>
                    <span className={stats.siteCount >= org.maxSites ? "org-val-warn" : ""}>{stats.siteCount}</span>
                    <span className="org-table-dim"> / {org.maxSites}</span>
                  </td>
                  <td>
                    <span className={stats.deviceCount >= org.maxDevices ? "org-val-warn" : ""}>{stats.deviceCount}</span>
                    <span className="org-table-dim"> / {org.maxDevices}</span>
                  </td>
                  <td>{stats.users}</td>
                  <td>
                    {stats.alertCount > 0
                      ? <span className="org-alert-chip">{stats.alertCount}</span>
                      : <span className="org-table-dim">—</span>
                    }
                  </td>
                  <td>
                    <div className="org-health-cell">
                      <HealthDot score={stats.health} />
                      <span>{stats.health}%</span>
                    </div>
                  </td>
                  <td><PlanBadge plan={org.plan} /></td>
                  <td><StatusBadge status={org.status} /></td>
                  <td className="org-table-dim">{org.createdAt}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="org-action-btns">
                      <button className="org-icon-btn" title="View" onClick={() => onSelect(org)}><FiEye size={14} /></button>
                      {canEditOrg(currentRole) && (
                        <button className="org-icon-btn" title="Edit" onClick={() => onEdit(org)}><MdEdit size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={11} className="org-empty-row">No organizations match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Root Component ───────────────────────────────────────────────────────────

export default function OrgManagement() {
  const [orgs, setOrgs]       = useState(siteDummyData.organizations);
  const [view, setView]       = useState("list");   // "list" | "detail"
  const [selectedOrg, selOrg] = useState(null);
  const [showModal, setModal] = useState(false);
  const [editOrg, setEditOrg] = useState(null);

  const openAdd  = ()      => { setEditOrg(null);  setModal(true); };
  const openEdit = (org)   => { setEditOrg(org);   setModal(true); };
  const openDash = (org)   => { selOrg(org);        setView("detail"); };

  const handleSave = (form) => {
    if (editOrg) {
      setOrgs(prev => prev.map(o => o.id === editOrg.id ? {
        ...o, name: form.name, type: form.type,
        registration: form.registration, gst: form.gst,
        address: form.address, contact: form.contact,
        email: form.email, phone: form.phone, description: form.description,
        plan: form.plan, maxSites: form.maxSites, maxDevices: form.maxDevices,
        solutionsEnabled: form.solutionsEnabled, dataRetention: form.dataRetention,
        slaLevel: form.slaLevel, apiAccess: form.apiAccess,
        adminUser: { name: form.adminName, email: form.adminEmail, role: "Org Admin" },
        timezone: form.timezone, notificationDefaults: form.notificationDefaults,
      } : o));
      if (selectedOrg?.id === editOrg.id) {
        selOrg(prev => ({ ...prev, name: form.name }));
      }
    } else {
      const newOrg = {
        id: `ORG-${String(orgs.length + 1).padStart(3, "0")}`,
        name: form.name, type: form.type,
        siteIds: [],
        registration: form.registration, gst: form.gst,
        address: form.address, contact: form.contact,
        email: form.email, phone: form.phone, description: form.description,
        plan: form.plan, maxSites: form.maxSites, maxDevices: form.maxDevices,
        solutionsEnabled: form.solutionsEnabled, dataRetention: form.dataRetention,
        slaLevel: form.slaLevel, apiAccess: form.apiAccess,
        status: "Active", createdAt: new Date().toISOString().slice(0, 10),
        adminUser: { name: form.adminName, email: form.adminEmail, role: "Org Admin" },
        timezone: form.timezone, notificationDefaults: form.notificationDefaults,
      };
      setOrgs(prev => [...prev, newOrg]);
    }
    setModal(false);
  };

  return (
    <div className="org-root">
      {view === "list" && (
        <OrgListView orgs={orgs} onSelect={openDash} onAdd={openAdd} onEdit={openEdit} />
      )}
      {view === "detail" && selectedOrg && (
        <OrgDashboardView
          org={orgs.find(o => o.id === selectedOrg.id) || selectedOrg}
          onBack={() => setView("list")}
          onEdit={openEdit}
        />
      )}
      {showModal && (
        <CreateOrgModal onClose={() => setModal(false)} onSave={handleSave} editOrg={editOrg} />
      )}
    </div>
  );
}
